import React, { useRef, useState } from 'react';

const PAD = { top: 16, right: 20, bottom: 40, left: 60 };

function buildPath(points, w, h, minY, maxY) {
    if (points.length === 0) return '';
    const rangeY = maxY - minY || 1;
    const xs = (i) => PAD.left + (i / (points.length - 1 || 1)) * (w - PAD.left - PAD.right);
    const ys = (v) => PAD.top + (1 - (v - minY) / rangeY) * (h - PAD.top - PAD.bottom);
    return points
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${xs(i).toFixed(1)},${ys(p.value).toFixed(1)}`)
        .join(' ');
}

function xAt(i, total, w) {
    return PAD.left + (i / (total - 1 || 1)) * (w - PAD.left - PAD.right);
}

function formatNum(v) {
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(0) + 'K';
    return String(Math.round(v));
}

function formatDate(dateStr) {
    // slice+T00:00:00 — чтобы календарная дата не уехала на день в браузерах
    // западнее UTC: "2026-08-30" без времени парсится как UTC-полночь.
    const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

/**
 * props:
 *   data        [{date, value}]           — daily data points
 *   milestones  [{date, label, metric}]   — milestone markers
 *   color       string                    — line colour
 *   label       string                    — series label
 *   milestoneOffset  number (days, default 2) — visual offset for milestone line
 */
export default function MilestoneLineChart({ data = [], milestones = [], color = '#3498db', label = '', milestoneOffset = 2 }) {
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null); // {x, y, content}

    const W = 700, H = 220;

    if (data.length === 0) {
        return (
            <div className="mline-empty">Нет данных за выбранный период</div>
        );
    }

    const values = data.map(d => d.value);
    const minY = Math.min(...values);
    const maxY = Math.max(...values);
    const path = buildPath(data.map(d => ({ value: d.value })), W, H, minY, maxY);

    // Y axis ticks
    const yTicks = 4;
    const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
        minY + (maxY - minY) * (i / yTicks)
    );

    // X axis ticks (every ~7 points or fewer if short)
    const step = Math.max(1, Math.floor(data.length / 7));
    const xTickIndices = data.map((_, i) => i).filter(i => i % step === 0 || i === data.length - 1);

    // Map milestones to x positions
    const dateIndex = {};
    data.forEach((d, i) => { dateIndex[d.date] = i; });

    const milestonePositions = milestones.map(m => {
        // find index of closest date on or after milestone date + offset
        const targetDate = new Date(m.date);
        targetDate.setDate(targetDate.getDate() + milestoneOffset);
        const targetStr = targetDate.toISOString().split('T')[0];

        // Find closest index
        let closestIdx = data.length - 1;
        for (let i = 0; i < data.length; i++) {
            if (data[i].date >= targetStr) { closestIdx = i; break; }
        }
        return { ...m, idx: closestIdx };
    });

    const rangeY = maxY - minY || 1;
    const ys = (v) => PAD.top + (1 - (v - minY) / rangeY) * (H - PAD.top - PAD.bottom);

    const handleMouseMove = (e) => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();
        const mx = (e.clientX - rect.left) * (W / rect.width);
        const chartW = W - PAD.left - PAD.right;
        const relX = mx - PAD.left;
        const idx = Math.round((relX / chartW) * (data.length - 1));
        if (idx < 0 || idx >= data.length) { setTooltip(null); return; }
        const pt = data[idx];
        const x = xAt(idx, data.length, W);
        const y = ys(pt.value);
        setTooltip({ x, y, date: pt.date, value: pt.value });
    };

    return (
        <div className="mline">
            <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                className="mline__svg"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setTooltip(null)}
            >
                {/* Y gridlines + ticks */}
                {yTickValues.map((v, i) => {
                    const y = ys(v);
                    return (
                        <g key={i}>
                            <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y}
                                stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
                            <text x={PAD.left - 6} y={y + 4} textAnchor="end"
                                fontSize="11" fill="currentColor" fillOpacity="0.5">
                                {formatNum(v)}
                            </text>
                        </g>
                    );
                })}

                {/* X axis ticks */}
                {xTickIndices.map(i => {
                    const x = xAt(i, data.length, W);
                    return (
                        <text key={i} x={x} y={H - PAD.bottom + 16}
                            textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.5">
                            {formatDate(data[i].date)}
                        </text>
                    );
                })}

                {/* X axis baseline */}
                <line
                    x1={PAD.left} x2={W - PAD.right}
                    y1={H - PAD.bottom} y2={H - PAD.bottom}
                    stroke="currentColor" strokeOpacity="0.15" strokeWidth="1"
                />

                {/* Data line */}
                <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

                {/* Area fill */}
                {data.length > 1 && (
                    <path
                        d={`${path} L${xAt(data.length - 1, data.length, W).toFixed(1)},${(H - PAD.bottom).toFixed(1)} L${PAD.left},${(H - PAD.bottom).toFixed(1)} Z`}
                        fill={color}
                        fillOpacity="0.08"
                    />
                )}

                {/* Milestone markers */}
                {milestonePositions.map((m, i) => {
                    const x = xAt(m.idx, data.length, W);
                    return (
                        <g key={i} className="mline__milestone">
                            <line
                                x1={x} x2={x}
                                y1={PAD.top} y2={H - PAD.bottom}
                                stroke="#f39c12"
                                strokeWidth="1.5"
                                strokeDasharray="4 3"
                                strokeOpacity="0.85"
                            />
                            <circle cx={x} cy={PAD.top + 2} r="5" fill="#f39c12" fillOpacity="0.9" />
                            <text x={x + 7} y={PAD.top + 6} fontSize="10" fill="#f39c12" fillOpacity="0.9">
                                {i + 1}
                            </text>
                        </g>
                    );
                })}

                {/* Hover crosshair + dot */}
                {tooltip && (
                    <g>
                        <line
                            x1={tooltip.x} x2={tooltip.x}
                            y1={PAD.top} y2={H - PAD.bottom}
                            stroke={color} strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 3"
                        />
                        <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={color} />
                    </g>
                )}
            </svg>

            {/* Tooltip */}
            {tooltip && (
                <div className="mline__tooltip" style={{
                    left: `${(tooltip.x / W) * 100}%`,
                }}>
                    <div className="mline__tooltip-date">{formatDate(tooltip.date)}</div>
                    <div className="mline__tooltip-val" style={{ color }}>{formatNum(tooltip.value)}</div>
                </div>
            )}

            {/* Milestone legend */}
            {milestonePositions.length > 0 && (
                <div className="mline__legend">
                    {milestonePositions.map((m, i) => (
                        <div key={i} className="mline__legend-item">
                            <span className="mline__legend-num">{i + 1}</span>
                            <span className="mline__legend-text">{m.label}</span>
                            <span className="mline__legend-date">{formatDate(m.date)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
